// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { UndoOutlined } from '@ant-design/icons';
import { SearchParams } from '@api/constants';
import {
  getWorkstationSettingsView,
  resetWorkstationSetting,
  updateWorkstationSetting,
} from '@api/portalServices';
import type { WorkstationSetting, WorkstationSettingsView } from '@api/types';
import { useSession } from '@common/authorization/context/Session';
import { ErrorCode } from '@errorHandler/constants';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { useEmitOnError } from '@errorHandler/context/useEmitOnError';
import { type ApiError, AppError, isSessionError } from '@errorHandler/errors';
import { Input } from '@imprivata-cloud/design-system';
import { EPIC_HYPERDRIVE_PRODUCT_ID } from '@portal/containers/applications/constants';
import { EamcPath, QueryKey } from '@portal/constants';
import { useStatusAnnouncer } from '@portal/hooks/useStatusAnnouncer';
import { withSearchParam } from '@utils/navigationSearch';
import {
  keepPreviousData,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { message, Spin } from 'antd';
import { X } from 'lucide-react';
import { type ReactNode, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import DurationField, { type DurationLimitProps, formatDuration } from './DurationField';
import FieldSelect from './FieldSelect';
import { keyFilter } from './keyFilter';
import { useProxCardMaxNumberMutation, useProxCardMaxNumberQuery } from './useProxCardMaxNumber';
import {
  acceptsValue,
  type Control,
  controlOf,
  durationLimitOf,
  limitErrorOf,
  fromHoursMinutes,
  isMalformed,
  isSectionId,
  isSettingId,
  overrideCount,
  type PageSection,
  pageSectionsOf,
  parseDecimal,
  SectionId,
  SettingId,
  type Status,
  statusOf,
  toHoursMinutes,
  withSetting,
} from './workstationSettingsModel';

const ROOT = 'portal.eamc.security';
const WS = `${ROOT}.workstation`;
// Between the parts of the group context line, as in the frame.
const SEPARATOR = ' · ';
const SECURITY = 'portal.security';
const SAVED_TOAST_KEY = 'workstation-settings-saved';
const MIN_PROX_CARDS = 1;
const MAX_PROX_CARDS = 99;
const MINUTES_PER_HOUR = 60;

const SECTION_KEYS: Record<SectionId, { title: string; description: string }> = {
  [SectionId.AUTHENTICATION]: {
    title: `${ROOT}.grace-period.title`,
    description: `${ROOT}.grace-period.description`,
  },
  [SectionId.DESKTOP]: { title: `${WS}.desktop.title`, description: `${WS}.desktop.description` },
  [SectionId.EPIC]: { title: `${WS}.epic.title`, description: `${WS}.epic.description` },
};

const SETTING_LABEL_KEYS: Record<SettingId, string> = {
  [SettingId.GRACE_PERIOD]: `${ROOT}.grace-period.title`,
  [SettingId.FADE_TIME]: `${WS}.desktop.fade-time`,
  [SettingId.INACTIVITY_TIMEOUT]: `${WS}.desktop.inactivity-timeout`,
  [SettingId.SHARED_LOCK]: `${WS}.epic.shared`,
  [SettingId.PRIVATE_LOCK]: `${WS}.epic.private`,
};

// The Applications page's Hyperdrive labels, so both pages name each code the same way.
const EPIC_OPTION_KEYS: Partial<Record<SettingId, string>> = {
  [SettingId.SHARED_LOCK]: 'apps.app-modal.epic-lock-config.shared.options',
  [SettingId.PRIVATE_LOCK]: 'apps.app-modal.epic-lock-config.private.options',
};

// `isSessionError` narrows the other branch to `never` for an `ApiError`.
const isSession = (error: ApiError): boolean => isSessionError(error);

const viewKey = (tenantId: string | undefined, groupId: string | undefined) => [
  QueryKey.WORKSTATION_SETTINGS,
  tenantId,
  groupId ?? null,
];

const range = (length: number) => Array.from({ length }, (_, i) => i);

const handleNumericKeyDown = keyFilter(/[0-9]/);

type MaxProxCardsError = 'required' | 'range' | null;

export function getMaxProxCardsError(value: number | undefined | null): MaxProxCardsError {
  if (value === undefined || value === null) {
    return 'required';
  }
  if (!Number.isInteger(value) || value < MIN_PROX_CARDS || value > MAX_PROX_CARDS) {
    return 'range';
  }
  return null;
}

const PROX_ERROR_KEYS = {
  required: 'portal.advanced-settings.fields.max-prox-cards.error-required',
  range: 'portal.advanced-settings.fields.max-prox-cards.error-range',
} as const;

const LIMIT_ERROR_KEYS = new Map<string, string>([
  [ErrorCode.GROUP_LIMIT_EXCEEDED, `${WS}.group-limit`],
  [ErrorCode.SETTING_LIMIT_EXCEEDED, `${WS}.setting-limit`],
]);

// `saved` takes the setting's name for the toast, for example "MFA grace period saved"
type Notify = { saved: (setting: string) => void; failed: (text: string) => void };

type Write = { kind: 'update'; value: string } | { kind: 'reset'; groupId: string };

type FieldProps = {
  setting: WorkstationSetting;
  groupId: string | undefined;
  label: string;
  disabled: boolean;
  notify: Notify;
  // The group was deleted elsewhere; the page goes back to the Default settings view.
  onGroupMissing: () => void;
};

// Lets the page read the writes still pending in a view; see durationLimitOf.
const writeKey = (groupId: string | undefined, settingId?: string) => [
  QueryKey.WORKSTATION_SETTINGS,
  'write',
  groupId ?? null,
  ...(settingId === undefined ? [] : [settingId]),
];

const isWrite = (value: unknown): value is Write =>
  typeof value === 'object' && value !== null && 'kind' in value;

// The values the pending writes in a view will save, by setting id; a reset saves the default.
const pendingValuesOf = (
  settings: readonly WorkstationSetting[],
  pending: readonly { settingId: unknown; write: unknown }[],
) => {
  const values = new Map<string, string[]>();
  for (const { settingId, write } of pending) {
    const setting = settings.find(candidate => candidate.id === settingId);
    if (!setting || !isWrite(write)) {
      continue;
    }
    const value = write.kind === 'update' ? write.value : setting.defaultValue;
    if (value !== undefined) {
      values.set(setting.id, [...(values.get(setting.id) ?? []), value]);
    }
  }
  return values;
};

const useSettingWrite = ({ setting, groupId, label, notify, onGroupMissing }: FieldProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { emitError } = useErrorContext();
  const { tenantId } = useSession();

  return useMutation<WorkstationSetting, ApiError, Write>({
    mutationKey: writeKey(groupId, setting.id),
    mutationFn: write =>
      (write.kind === 'reset'
        ? resetWorkstationSetting({ groupId: write.groupId, settingId: setting.id })
        : updateWorkstationSetting({ groupId, settingId: setting.id, value: write.value })
      ).then(response => response.setting),
    // Writes to one setting run in order, so the last choice is the one that sticks.
    scope: { id: `${QueryKey.WORKSTATION_SETTINGS}:${setting.id}` },
    onSuccess: next => {
      const key = viewKey(tenantId, groupId);
      queryClient.setQueryData<WorkstationSettingsView>(
        key,
        view => view && withSetting(view, next),
      );
      // A default feeds every group view, and an override changes no other view, so the other
      // cached views are only marked stale.
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.WORKSTATION_SETTINGS],
        predicate: query => query.queryKey[2] !== key[2],
        refetchType: 'none',
      });
      notify.saved(label);
    },
    onError: error => {
      if (isSession(error)) {
        emitError(new AppError(error));
        return;
      }
      console.error('[workflows] save failed', setting.id, error.code);
      if (error.code === ErrorCode.GROUP_NOT_FOUND) {
        notify.failed(t(`${WS}.group-missing`));
        onGroupMissing();
        return;
      }
      const limitKey = LIMIT_ERROR_KEYS.get(error.code);
      notify.failed(limitKey ? t(limitKey) : t(`${ROOT}.save-failed`, { settings: label }));
      // A failed write can be partly applied, so the view is read again.
      if ((error.httpStatus ?? 0) >= 500) {
        void queryClient.invalidateQueries({ queryKey: viewKey(tenantId, groupId) });
      }
    },
  });
};

type StatusLineProps = {
  id: string;
  status: Status;
  label: string;
  busy: boolean;
  onReset: () => void;
};

const StatusLine = ({ id, status, label, busy, onReset }: StatusLineProps) => {
  const { t } = useTranslation();
  switch (status.kind) {
    case 'none':
      return null;
    case 'default':
      return (
        <p id={id} className="workstation-status">
          {t(`${WS}.status.default`)}
        </p>
      );
    case 'override':
      return (
        <p id={id} className="workstation-status workstation-status-override">
          {t(`${WS}.status.override`)}
          {status.canReset && (
            <button
              type="button"
              className="workstation-reset"
              aria-label={t(`${WS}.status.reset-label`, { setting: label })}
              aria-busy={busy}
              onClick={() => {
                if (!busy) {
                  onReset();
                }
              }}
            >
              <UndoOutlined aria-hidden />
              {t(`${WS}.status.reset`)}
            </button>
          )}
        </p>
      );
  }
};

type NumberFieldProps = {
  label: string;
  ariaLabel: string;
  value: number;
  accepts: (value: number) => boolean;
  errorText: (value: number | null) => string;
  hint?: string;
  describedBy?: string;
  disabled: boolean;
  // Called with a valid, changed value; `revert` puts the saved value back after a failure.
  onCommit: (value: number, revert: () => void) => void;
};

// Saves on blur or Enter, never per keystroke, so typing "15" does not save "1" first.
const NumberField = ({
  label,
  ariaLabel,
  value,
  accepts,
  errorText,
  hint,
  describedBy,
  disabled,
  onCommit,
}: NumberFieldProps) => {
  const inputId = useId();
  const hintId = useId();
  const errorId = useId();
  // The draft follows the saved value whenever that changes, without an effect. It is stored, not
  // just derived, or a stale draft would come back when the value returns to its old base (Reset).
  const [draft, setDraft] = useState({ base: value, text: String(value), invalid: false });
  let current = draft;
  if (draft.base !== value) {
    current = { base: value, text: String(value), invalid: false };
    setDraft(current);
  }

  const commit = () => {
    const parsed = parseDecimal(current.text);
    if (parsed === null || !accepts(parsed)) {
      setDraft({ ...current, invalid: true });
      return;
    }
    setDraft({ ...current, invalid: false });
    if (parsed !== value) {
      onCommit(parsed, () => setDraft({ base: value, text: String(value), invalid: false }));
    }
  };

  return (
    <div className="workstation-number">
      <Input
        id={inputId}
        className="workstation-input"
        label={label}
        aria-label={ariaLabel}
        // Replaces the DS Input's own description, whose error id is generated and can't be
        // listed, so the error message is rendered below instead.
        aria-describedby={
          [current.invalid ? errorId : undefined, hint ? hintId : undefined, describedBy]
            .filter(Boolean)
            .join(' ') || undefined
        }
        value={current.text}
        disabled={disabled}
        inputMode="numeric"
        error={current.invalid}
        onKeyDown={event => {
          handleNumericKeyDown(event);
          if (event.key === 'Enter') {
            commit();
          }
        }}
        onChange={event => setDraft({ ...current, text: event.target.value })}
        onBlur={commit}
      />
      {current.invalid && (
        <p id={errorId} className="workstation-error">
          {errorText(parseDecimal(current.text))}
        </p>
      )}
      {hint && (
        <p id={hintId} className="workstation-hint">
          {hint}
        </p>
      )}
    </div>
  );
};

type ControlFieldProps = FieldProps & {
  control: Control;
  limit?: DurationLimitProps;
  footer?: ReactNode;
};

const SettingField = (props: ControlFieldProps) => {
  const { t } = useTranslation();
  const { setting, groupId, label, disabled, control, limit, notify, footer } = props;
  const statusId = useId();
  const write = useSettingWrite(props);
  const status = statusOf(groupId, setting);
  const readOnly = disabled || !setting.actions.edit;
  const describedBy = status.kind === 'none' ? undefined : statusId;
  const update = (value: string, revert?: () => void) =>
    write.mutate({ kind: 'update', value }, { onError: revert });
  // A pending choice shows at once; a failure drops back to the saved value.
  const shown =
    write.isPending && write.variables.kind === 'update'
      ? write.variables.value
      : setting.effectiveValue;

  const field = (() => {
    switch (control.kind) {
      case 'duration':
        return (
          <DurationField
            label={label}
            value={control.seconds}
            range={control.range}
            presets={control.presets}
            limit={limit}
            describedBy={describedBy}
            disabled={readOnly}
            onCommit={(seconds, revert) => update(String(seconds), revert)}
          />
        );
      case 'hours-minutes': {
        const seconds = parseDecimal(shown) ?? control.seconds;
        const { hours, minutes } = toHoursMinutes(seconds);
        const choose = (nextHours: number, nextMinutes: number) => {
          // Picking the top hour keeps the minutes within the range.
          const next = String(Math.min(fromHoursMinutes(nextHours, nextMinutes), control.max));
          if (next !== shown && acceptsValue(setting, next)) {
            update(next);
          }
        };
        return (
          <div className="workstation-hours-minutes">
            <FieldSelect
              label={t(`${SECURITY}.step-up.hours`)}
              hideLabel
              describedBy={describedBy}
              value={String(hours)}
              disabled={readOnly}
              options={range(control.maxHours + 1).map(count => ({
                value: String(count),
                label: t(`${ROOT}.grace-period.hours-option`, { count }),
              }))}
              onChange={value => choose(Number(value), minutes)}
            />
            <FieldSelect
              label={t(`${SECURITY}.step-up.minutes`)}
              hideLabel
              describedBy={describedBy}
              value={String(minutes)}
              disabled={readOnly}
              options={range(MINUTES_PER_HOUR).map(count => ({
                value: String(count),
                label: t(`${ROOT}.grace-period.minutes-option`, { count }),
              }))}
              onChange={value => choose(hours, Number(value))}
            />
          </div>
        );
      }
      case 'choice': {
        const optionsKey = isSettingId(setting.id) ? EPIC_OPTION_KEYS[setting.id] : undefined;
        // The BFF's code as it is: no code is given a meaning of its own (user, 2026-10-16, Q12).
        // An empty code has no key to look up, so it stays empty.
        const optionLabel = (value: string) =>
          optionsKey && value ? t(`${optionsKey}.${value}`, { defaultValue: value }) : value;
        return (
          <FieldSelect
            label={label}
            describedBy={describedBy}
            value={shown}
            disabled={readOnly}
            options={control.values.map(value => ({ value, label: optionLabel(value) }))}
            onChange={update}
          />
        );
      }
      case 'generic': {
        const numeric = control.range && parseDecimal(control.value);
        if (control.range && numeric !== null && numeric !== undefined) {
          const { min, max } = control.range;
          return (
            <NumberField
              label={label}
              ariaLabel={label}
              value={numeric}
              accepts={value => acceptsValue(setting, String(value))}
              errorText={() => t(`${WS}.desktop.range-error`, { min, max })}
              hint={t('portal.password-policy.fields.range', { min, max })}
              describedBy={describedBy}
              disabled={readOnly}
              onCommit={(value, revert) => update(String(value), revert)}
            />
          );
        }
        return (
          <FieldSelect
            label={label}
            describedBy={describedBy}
            value={shown}
            disabled={readOnly}
            options={control.values.map(value => ({ value, label: value }))}
            onChange={update}
          />
        );
      }
    }
  })();

  return (
    <div className="workstation-field">
      {field}
      <StatusLine
        id={statusId}
        status={status}
        label={label}
        busy={write.isPending}
        onReset={() => {
          if (groupId === undefined) {
            return;
          }
          // A reset that would break the fade and inactivity pair is not sent.
          const resetTo =
            setting.defaultValue === undefined ? null : parseDecimal(setting.defaultValue);
          const error =
            control.kind === 'duration' && resetTo !== null ? limitErrorOf(limit, resetTo) : null;
          if (error && limit) {
            notify.failed(
              t(`${WS}.desktop.reset-${error}`, {
                setting: limit.label,
                value: formatDuration(limit.seconds),
              }),
            );
            return;
          }
          write.mutate({ kind: 'reset', groupId });
        }}
      />
      {footer}
    </div>
  );
};

type ProxCardsProps = { readOnly: boolean; notify: Notify };

const ProxCards = ({ readOnly, notify }: ProxCardsProps) => {
  const { t } = useTranslation();
  const { emitError } = useErrorContext();
  const prox = useProxCardMaxNumberQuery();
  const mutation = useProxCardMaxNumberMutation();
  const label = t(`${ROOT}.prox-cards.label`);
  // A failed read shows as the global error toast, as on the other pages, and the field is left out.
  useEmitOnError(prox.error);

  const saved = prox.data?.proxCardMaxNumber;
  const body = (() => {
    if (prox.isPending) {
      return <Spin className="workflows-loading" />;
    }
    if (saved === undefined) {
      return null;
    }
    return (
      <NumberField
        label={label}
        ariaLabel={label}
        value={saved}
        accepts={value => getMaxProxCardsError(value) === null}
        errorText={value => t(PROX_ERROR_KEYS[getMaxProxCardsError(value) ?? 'range'])}
        hint={t(`${ROOT}.prox-cards.helper`)}
        disabled={readOnly || mutation.isPending}
        onCommit={(proxCardMaxNumber, revert) =>
          mutation.mutate(
            { proxCardMaxNumber },
            {
              onSuccess: () => notify.saved(t(`${ROOT}.prox-cards.saved-name`)),
              onError: error => {
                revert();
                if (isSession(error)) {
                  emitError(new AppError(error));
                  return;
                }
                console.error('[workflows] save failed', 'prox-cards', error.code);
                notify.failed(
                  t(`${ROOT}.save-failed`, { settings: t(`${ROOT}.prox-cards.title`) }),
                );
              },
            },
          )
        }
      />
    );
  })();

  return (
    body && (
      <SubSection id="prox-cards" title={t(`${ROOT}.prox-cards.title`)}>
        {body}
      </SubSection>
    )
  );
};

type SubSectionProps = { id: string; title: string; description?: string; children: ReactNode };

const SubSection = ({ id, title, description, children }: SubSectionProps) => {
  const titleId = useId();
  const descriptionId = useId();
  return (
    <fieldset
      className={`workstation-subsection workstation-subsection-${id}`}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
    >
      <h4 id={titleId}>{title}</h4>
      {description && <p id={descriptionId}>{description}</p>}
      {children}
    </fieldset>
  );
};

const settingLabel = (t: (key: string) => string, setting: WorkstationSetting) =>
  isSettingId(setting.id) ? t(SETTING_LABEL_KEYS[setting.id]) : setting.id;

const WorkstationSettings = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { tenantId } = useSession();
  const [announce, statusRegion] = useStatusAnnouncer();
  const [messageApi, contextHolder] = message.useMessage();
  const queryClient = useQueryClient();
  const { emitError } = useErrorContext();
  // Undefined is the Default settings view, as in the contract. Not kept in the URL, so the page
  // always opens on the Default settings view (user, 2026-10-16).
  const [groupId, setGroupId] = useState<string>();

  const view = useQuery<WorkstationSettingsView, ApiError>({
    queryKey: viewKey(tenantId, groupId),
    queryFn: () => getWorkstationSettingsView({ groupId }).then(response => response.view),
    // Switching groups keeps the last view on screen, disabled, until the next one arrives.
    placeholderData: keepPreviousData,
    retry: false,
  });
  const pendingWrites = useMutationState({
    filters: { mutationKey: writeKey(groupId), status: 'pending' },
    select: mutation => ({
      settingId: mutation.options.mutationKey?.[3],
      write: mutation.state.variables,
    }),
  });
  // A Default settings view that fails to load shows the global error toast, as on the other
  // pages, and no settings (user, 2026-10-16). A group view that fails to load falls back to the
  // Default settings view with the same toast; a deleted group falls back without one.
  useEmitOnError(
    view.error && (groupId === undefined || isSessionError(view.error)) ? view.error : null,
  );
  const groupFailed =
    groupId !== undefined && !!view.error && !view.isFetching && !isSessionError(view.error);
  useEffect(() => {
    if (!groupFailed || !view.error) {
      return;
    }
    if (view.error.code !== ErrorCode.GROUP_NOT_FOUND) {
      console.error('[workflows] group view failed', groupId, view.error.code);
      emitError(new AppError(view.error));
    }
    // Choosing the group again then loads it afresh, with the default view still on screen.
    queryClient.removeQueries({ queryKey: viewKey(tenantId, groupId), exact: true });
    setGroupId(undefined);
  }, [groupFailed, view.error, groupId, tenantId, emitError, queryClient]);

  const malformed = (view.data?.sections ?? [])
    .flatMap(section => section.settings)
    .filter(isMalformed)
    .map(setting => setting.id)
    .join(', ');
  useEffect(() => {
    if (malformed) {
      console.error('[workflows] invalid settings', malformed);
    }
  }, [malformed]);

  const notify: Notify = {
    saved: setting => {
      const text = t(`${WS}.saved`, { setting });
      void messageApi.open({
        key: SAVED_TOAST_KEY,
        type: 'success',
        content: (
          <span className="workflows-toast">
            {text}
            <button
              type="button"
              className="workflows-toast-close"
              aria-label={t('common.close')}
              onClick={() => messageApi.destroy(SAVED_TOAST_KEY)}
            >
              <X aria-hidden size={16} />
            </button>
          </span>
        ),
      });
      announce(text);
    },
    failed: text => {
      void messageApi.error({ content: text });
      announce(text);
    },
  };

  const selectGroup = (next: string) => setGroupId(next || undefined);
  const leaveGroup = () => setGroupId(undefined);

  const body = (() => {
    if (view.isPending || groupFailed) {
      return <Spin className="workflows-loading" />;
    }
    if (!view.data) {
      return null;
    }
    const { groups } = view.data;
    const group = groups.find(candidate => candidate.groupId === groupId);
    const stale = view.isPlaceholderData;
    const inGroup = groupId !== undefined;

    // Under each Epic select in the Default settings view, as in the frames, since both come from
    // the Hyperdrive settings. A group view has no link (design decision, 2026-10-15).
    const applicationsLink = (
      <Link
        className="workstation-link"
        // Applications opens the Epic Hyperdrive Edit modal, or its Add modal if Epic Hyperdrive
        // is not set up (Q17).
        to={withSearchParam(
          EamcPath.APPLICATIONS,
          searchParams,
          SearchParams.EDIT_APP,
          EPIC_HYPERDRIVE_PRODUCT_ID,
        )}
      >
        {t(`${WS}.epic.applications-link`)}
      </Link>
    );

    const pendingValues = pendingValuesOf(
      view.data.sections.flatMap(section => section.settings),
      pendingWrites,
    );

    const sectionOf = (section: PageSection) => {
      const keys = isSectionId(section.id) ? SECTION_KEYS[section.id] : undefined;
      return (
        <SubSection
          key={section.id}
          id={section.id}
          title={keys ? t(keys.title) : section.id}
          description={keys ? t(keys.description) : undefined}
        >
          <div className={`workstation-fields workstation-fields-${section.id}`}>
            {section.settings.map(setting => {
              const limit = durationLimitOf(section.settings, setting, pendingValues);
              return (
                <SettingField
                  key={`${groupId ?? ''}:${setting.id}`}
                  setting={setting}
                  groupId={groupId}
                  label={settingLabel(t, setting)}
                  disabled={stale}
                  control={controlOf(setting)}
                  limit={
                    limit && {
                      bound: limit.bound,
                      seconds: limit.seconds,
                      label: settingLabel(t, limit.setting),
                    }
                  }
                  notify={notify}
                  onGroupMissing={leaveGroup}
                  footer={section.id === SectionId.EPIC && !inGroup ? applicationsLink : undefined}
                />
              );
            })}
          </div>
        </SubSection>
      );
    };

    // Proximity cards are not part of this API; they sit after the MFA grace period.
    const pageSections = pageSectionsOf(view.data);
    const authentication = pageSections.filter(section => section.id === SectionId.AUTHENTICATION);
    const others = pageSections.filter(section => section.id !== SectionId.AUTHENTICATION);

    return (
      <>
        <FieldSelect
          className="workstation-group-select"
          label={t(`${WS}.group-label`)}
          value={groupId ?? ''}
          options={[
            { value: '', label: t(`${WS}.default-option`) },
            ...groups.map(candidate => ({ value: candidate.groupId, label: candidate.name })),
          ]}
          onChange={selectGroup}
        />
        <div className="workstation-context">
          {inGroup ? (
            <>
              <h3 title={group?.name}>{group?.name}</h3>
              <p>
                {group && (
                  <>
                    {t(`${WS}.workstations`, { count: group.workstationCount })}
                    {SEPARATOR}
                  </>
                )}
                <strong>{t(`${WS}.overrides`, { count: overrideCount(view.data) })}</strong>
                {SEPARATOR}
                {t(`${WS}.group-description`)}
              </p>
            </>
          ) : (
            <>
              <h3>{t(`${WS}.default-option`)}</h3>
              <p>{t(`${WS}.default-description`)}</p>
            </>
          )}
        </div>
        {authentication.map(sectionOf)}
        {/* Proximity cards apply to every workstation, so a group view shows them read-only. */}
        <ProxCards readOnly={inGroup || stale} notify={notify} />
        {others.map(sectionOf)}
      </>
    );
  })();

  return (
    <section className="workstation-settings" aria-labelledby="workstation-settings-title">
      {statusRegion}
      {contextHolder}
      <h2 id="workstation-settings-title">{t(`${WS}.title`)}</h2>
      <p>{t(`${WS}.intro`)}</p>
      {body}
    </section>
  );
};

export default WorkstationSettings;
